import { ArrowheadDown, ArrowheadRight } from '@elliemae/ds-icons';
import { SimpleTruncatedTooltipText } from '@elliemae/ds-truncated-tooltip-text';
import React, { useMemo } from 'react';
import { ITEM_TYPES, LEFT_NAVIGATION_DATA_TESTID } from '../../constants/index.js';
import { ItemRenderer } from '../../exported-related/index.js';
import type { DSLeftNavigationT } from '../../react-desc-prop-types.js';
import { ItemSeparator } from '../ItemSeparator/index.js';
import { ItemTextLabel } from '../ItemTextLabel/index.js';
import { ItemWithDate } from '../ItemWithDate/index.js';
import { StyledArrowContainer, StyledDate, StyledLabel, StyledRightContainer } from '../styled.js';

const outOfTheBoxSubitemsComponents: Record<string, React.ComponentType<DSLeftNavigationT.ComponentProps>> = {
  [ITEM_TYPES.LEFT_NAV_ITEM_SEPARATOR]: ItemSeparator,
  [ITEM_TYPES.LEFT_NAV_ITEM_WITH_DATE]: ItemWithDate,
  [ITEM_TYPES.LEFT_NAV_ITEM_TEXT_LABEL]: ItemTextLabel,
};

const OutOfTheBoxMapSubitem: React.ComponentType<DSLeftNavigationT.ComponentProps> = (props) => {
  const {
    item,
    item: { type, Component },
    ctx,
  } = props;

  if (typeof type === 'string' && !!outOfTheBoxSubitemsComponents[type]) {
    const OutOfTheBoxComponent = outOfTheBoxSubitemsComponents[type];
    const currentIndent = item.itemOpts?.indent ?? 0;
    const newItem = {
      ...item,
      itemOpts: { ...item.itemOpts, indent: currentIndent + 44 },
    };
    return <OutOfTheBoxComponent item={newItem} ctx={ctx} />;
  }
  if (!Component) return null;
  // we expect an error to be threw if Component is not defined and type is not a valid out-of-the-box...
  return <Component item={item} ctx={ctx} />;
};

export const ItemControlledDrilldown = (props: DSLeftNavigationT.ComponentProps): JSX.Element => {
  const {
    item,
    item: {
      itemOpts: {
        label,
        RightComponent,
        labelRightSection,
        labelOverflow: itemLabelOverflow,
        labelBold,
        labelColor,
        isOpened,
        items,
      },
    },
    ctx,
    ctx: {
      leftNavProps,
      leftNavProps: { labelOverflow: leftNavLabelOverflow },
    },
  } = props;
  const getOwnerProps = React.useCallback(() => leftNavProps, [leftNavProps]);
  const getOwnerPropsArguments = React.useCallback(() => ({}), []);

  const labelOverflow = itemLabelOverflow || leftNavLabelOverflow;

  const arrowIcon = useMemo(() => {
    const Component = isOpened ? ArrowheadDown : ArrowheadRight;
    return <Component width="24px" height="18px" color={['brand-primary', '800']} />;
  }, [isOpened]);

  const subitems = useMemo(() => {
    if (!isOpened || !items) return [];
    return items.map((subitem) => <OutOfTheBoxMapSubitem item={subitem} ctx={ctx} key={subitem.dsId} />);
  }, [isOpened, items, ctx]);

  return (
    <ItemRenderer
      item={{
        ...item,
        itemOpts: {
          ariaExpanded: isOpened,
          ariaRole: 'menuitem',
          ...item.itemOpts,
          openable: !isOpened,
          closable: isOpened,
        },
        CollapsedComponent: null,
      }}
      subitems={subitems}
      minHeight="32px"
      paddingTop="8px"
    >
      <StyledArrowContainer
        pl="xxs"
        mt="-2px"
        data-testid={LEFT_NAVIGATION_DATA_TESTID.ARROW_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {arrowIcon}
      </StyledArrowContainer>
      <StyledLabel
        pb="xxxs"
        labelOverflow={labelOverflow}
        labelBold={labelBold}
        labelColor={labelColor}
        data-testid={LEFT_NAVIGATION_DATA_TESTID.LIST_ITEM_LABEL_ID_EXCEPTION}
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {labelOverflow === 'truncate' ? <SimpleTruncatedTooltipText value={label} placement="bottom-start" /> : label}
      </StyledLabel>
      <StyledRightContainer
        pr="xxs2"
        cols={['auto', 'auto']}
        alignItems="center"
        gutter="xxs"
        data-testid={LEFT_NAVIGATION_DATA_TESTID.RIGHT_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        <StyledDate
          ml="xxs"
          data-testid={LEFT_NAVIGATION_DATA_TESTID.ITEM_DATE} // https://jira.elliemae.io/browse/PUI-15589
          getOwnerProps={getOwnerProps}
          getOwnerPropsArguments={getOwnerPropsArguments}
        >
          {labelRightSection}
        </StyledDate>
        {RightComponent && <RightComponent ctx={ctx} item={item} />}
      </StyledRightContainer>
    </ItemRenderer>
  );
};
